import { useState, useEffect } from "react";
import { X, Loader2, CheckCircle2, AlertCircle, Shield } from "lucide-react";
import { testEdcConnection } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { useConfigureConnection, useTestConnection, useTriggerSync } from "@/hooks/use-integrations";
import { useAutoImportStudyDesign } from "@/hooks/use-edc-export";
import type { IntegrationBranding, EdcConnection } from "@/api/types";

const CREDENTIAL_FIELDS: Record<string, Array<{ name: string; label: string; type: string; required?: boolean }>> = {
  veeva: [
    { name: "username", label: "Username", type: "text", required: true },
    { name: "password", label: "Password", type: "password", required: true },
  ],
  medrio: [
    { name: "username", label: "Username", type: "text", required: true },
    { name: "password", label: "Password", type: "password", required: true },
    { name: "customerApiKey", label: "Customer API Key", type: "password" },
    { name: "studyApiKey", label: "Study API Key (Export)", type: "password" },
  ],
  redcap: [
    { name: "apiToken", label: "API Token", type: "password", required: true },
  ],
  redcap_cloud: [
    { name: "apiToken", label: "API Token", type: "password", required: true },
  ],
  imednet: [
    { name: "apiKey", label: "API Key", type: "password", required: true },
    { name: "securityKey", label: "Security Key", type: "password", required: true },
  ],
  medidata: [
    { name: "clientId", label: "Client ID", type: "text", required: true },
    { name: "clientSecret", label: "Client Secret", type: "password", required: true },
  ],
  castor: [
    { name: "clientId", label: "Client ID", type: "text", required: true },
    { name: "clientSecret", label: "Client Secret", type: "password", required: true },
  ],
};

const AUTH_METHOD_MAP: Record<string, string> = {
  veeva: "session",
  medrio: "oauth2",
  redcap: "token",
  redcap_cloud: "token",
  imednet: "api_key",
  medidata: "oauth2",
  castor: "oauth2",
};

const SCHEDULE_OPTIONS = [
  { value: "every 1 hours", label: "Every hour" },
  { value: "every 6 hours", label: "Every 6 hours" },
  { value: "every 12 hours", label: "Every 12 hours" },
  { value: "every 24 hours", label: "Every 24 hours" },
];

type Phase = "form" | "saving" | "testing" | "syncing" | "importing" | "success" | "error";

interface ConfigureConnectionDialogProps {
  integration: IntegrationBranding;
  onClose: () => void;
  linkedStudyId?: string;
  connection?: EdcConnection;
}

export function ConfigureConnectionDialog({ integration, onClose, linkedStudyId, connection }: ConfigureConnectionDialogProps) {
  const isEditMode = !!connection;
  const [label, setLabel] = useState(connection?.label ?? integration.name);
  const [baseUrl, setBaseUrl] = useState(connection?.baseUrl ?? "");
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [syncSchedule, setSyncSchedule] = useState(connection?.syncSchedule ?? "every 6 hours");
  const [environment, setEnvironment] = useState<"test" | "staging" | "production">(connection?.environment ?? "production");
  const [selectedScopes, setSelectedScopes] = useState<string[]>(
    connection?.syncScope ?? integration.supportedScopes ?? []
  );
  const [selectedStudyId, setSelectedStudyId] = useState(connection?.linkedStudyId ? "" : "");
  const [availableStudies, setAvailableStudies] = useState<Array<{id: string; name: string}>>([]);
  const [studiesLoading, setStudiesLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>("form");
  const [error, setError] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState("");

  const queryClient = useQueryClient();
  const configureMutation = useConfigureConnection();
  const testMutation = useTestConnection();
  const syncMutation = useTriggerSync();
  const importMutation = useAutoImportStudyDesign();

  const credFields = CREDENTIAL_FIELDS[integration.id] ?? [];
  const authMethod = AUTH_METHOD_MAP[integration.id] ?? "session";

  // In edit mode for Medrio, auto-fetch available studies for the dropdown
  useEffect(() => {
    if (isEditMode && connection && integration.id === "medrio") {
      setStudiesLoading(true);
      testEdcConnection({ connectionId: connection.id })
        .then((res) => {
          if (res.availableStudies) {
            setAvailableStudies(res.availableStudies);
          }
        })
        .catch(() => { /* non-blocking */ })
        .finally(() => setStudiesLoading(false));
    }
  }, [isEditMode, connection, integration.id]);

  function toggleScope(scope: string) {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  }

  function setCredField(name: string, value: string) {
    setCredentials((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit() {
    setError(null);

    if (!label.trim()) { setError("Label is required."); return; }
    if (!baseUrl.trim()) { setError("Base URL is required."); return; }

    if (!isEditMode) {
      const missingCreds = credFields
        .filter((f) => f.required !== false && !credentials[f.name]?.trim())
        .map((f) => f.label);
      if (missingCreds.length > 0) {
        setError(`Missing required credentials: ${missingCreds.join(", ")}`);
        return;
      }
    }

    try {
      // Phase 1: Configure
      setPhase("saving");
      // In edit mode, only send credentials that were filled in
      const credsToSend = isEditMode
        ? Object.fromEntries(Object.entries(credentials).filter(([, v]) => v.trim()))
        : credentials;
      const studyIdVal = selectedStudyId || undefined;
      const result = await configureMutation.mutateAsync({
        system: integration.id,
        label: label.trim(),
        baseUrl: baseUrl.trim(),
        credentials: credsToSend,
        authMethod,
        syncScope: selectedScopes,
        syncSchedule,
        ...(studyIdVal ? { studyId: studyIdVal, studyFilter: [studyIdVal] } : {}),
        ...(linkedStudyId ? { linkedStudyId } : {}),
        ...(isEditMode && connection ? { connectionId: connection.id } : {}),
        environment,
      });

      // Phase 2: Test
      setPhase("testing");
      const testResult = await testMutation.mutateAsync({
        connectionId: result.connectionId,
      });

      if (testResult.status === "active") {
        const successMsg = testResult.message;
        setTestMessage(successMsg);

        // Phase 3+4: Auto-sync and import if a linked study exists
        const studyId = linkedStudyId ?? connection?.linkedStudyId;
        if (studyId) {
          try {
            setPhase("syncing");
            const syncResult = await syncMutation.mutateAsync({ connectionId: result.connectionId });

            // Check if sync returned a non-throwing error
            const syncError = (syncResult as Record<string, unknown>)?.error;

            if (!syncError && integration.id === "medrio") {
              setPhase("importing");
              try {
                const importResult = await importMutation.mutateAsync({
                  studyId,
                  connectionId: result.connectionId,
                });
                setTestMessage(
                  `${successMsg} Imported ${importResult.crfCount} CRFs and ${importResult.visitCount} visits.`
                );
              } catch {
                setTestMessage(
                  `${successMsg} Study design import encountered an issue — you can import from the study page.`
                );
              }
            } else if (syncError) {
              setTestMessage(
                `${successMsg} Data sync encountered an issue — you can retry from the study page.`
              );
            }
          } catch {
            // Sync mutation threw — still non-fatal, connection is active
            setTestMessage(
              `${successMsg} Data sync or import encountered an issue — you can retry from the study page.`
            );
          }

          // Refresh study page data
          queryClient.invalidateQueries({ queryKey: ["study"] });
          queryClient.invalidateQueries({ queryKey: ["studyBuilds"] });
          queryClient.invalidateQueries({ queryKey: ["edcConnections"] });
        }

        setPhase("success");
      } else {
        setTestMessage(testResult.message);
        setPhase("error");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to configure connection");
      setPhase("error");
    }
  }

  const isSubmitting = phase === "saving" || phase === "testing" || phase === "syncing" || phase === "importing";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded text-xs font-bold text-white"
              style={{ backgroundColor: integration.brandColor }}
            >
              {(integration.shortName ?? integration.id).slice(0, 2)}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {isEditMode ? "Edit" : "Configure"} {integration.name}
              </h3>
              <p className="text-xs text-muted-foreground">
                {integration.authMethod ?? "credentials"} authentication
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Success State */}
          {phase === "success" && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3 py-4">
                <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                <h4 className="text-lg font-semibold text-foreground">Connection Active</h4>
                <p className="text-sm text-muted-foreground text-center">{testMessage}</p>
              </div>
              <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                <Shield className="h-3.5 w-3.5 shrink-0" />
                Credentials stored securely in Google Cloud Secret Manager
              </div>
              <button
                onClick={onClose}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Done
              </button>
            </div>
          )}

          {/* Error State (post-submit) */}
          {phase === "error" && !error && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3 py-4">
                <AlertCircle className="h-12 w-12 text-red-500" />
                <h4 className="text-lg font-semibold text-foreground">Connection Test Failed</h4>
                <p className="text-sm text-muted-foreground text-center">{testMessage}</p>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                The connection was saved but could not be validated. Check your credentials and base URL, then test again from the Integrations page.
              </p>
              <button
                onClick={onClose}
                className="w-full rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Close
              </button>
            </div>
          )}

          {/* Form State */}
          {(phase === "form" || phase === "saving" || phase === "testing" || phase === "syncing" || phase === "importing" || (phase === "error" && error)) && (
            <>
              {/* Setup Steps */}
              {integration.setupSteps?.length > 0 && (
                <div className="rounded-md border border-border bg-muted/30 p-4">
                  <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Setup Steps
                  </h4>
                  <ol className="space-y-1.5 text-xs text-muted-foreground">
                    {integration.setupSteps?.map((step, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="shrink-0 font-medium text-foreground">{i + 1}.</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Error Banner */}
              {error && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Connection Label</label>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    disabled={isSubmitting}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Base URL</label>
                  <input
                    type="url"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder={`https://your-instance.${integration.id.replace("_", "")}.com`}
                    disabled={isSubmitting}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                  />
                </div>

                {/* Environment */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Environment</label>
                  <select
                    value={environment}
                    onChange={(e) => setEnvironment(e.target.value as "test" | "staging" | "production")}
                    disabled={isSubmitting}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                  >
                    <option value="production">Production</option>
                    <option value="test">Test</option>
                    <option value="staging">Staging</option>
                  </select>
                </div>

                {/* Credential Fields */}
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-foreground">Credentials</h4>
                  <div className="space-y-3">
                    {credFields.map((field) => (
                      <div key={field.name} className="space-y-1">
                        <label className="text-xs text-muted-foreground">
                          {field.label}
                          {field.required !== false && !isEditMode && <span className="text-destructive"> *</span>}
                        </label>
                        <input
                          type={field.type}
                          value={credentials[field.name] ?? ""}
                          onChange={(e) => setCredField(field.name, e.target.value)}
                          placeholder={isEditMode ? "••••••••  (leave blank to keep current)" : undefined}
                          disabled={isSubmitting}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Medrio Study Selector */}
                {integration.id === "medrio" && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-foreground">Medrio Study</label>
                    {studiesLoading ? (
                      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading studies from Medrio...
                      </div>
                    ) : availableStudies.length > 0 ? (
                      <select
                        value={selectedStudyId}
                        onChange={(e) => setSelectedStudyId(e.target.value)}
                        disabled={isSubmitting}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                      >
                        <option value="">Select a study...</option>
                        {availableStudies.map((s) => (
                          <option key={s.id} value={s.id}>{s.name} ({s.id})</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={selectedStudyId}
                        onChange={(e) => setSelectedStudyId(e.target.value)}
                        placeholder="Enter Medrio Study ID"
                        disabled={isSubmitting}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                      />
                    )}
                    <p className="text-[10px] text-muted-foreground">Credentials in Medrio are per-study</p>
                  </div>
                )}

                {/* Sync Scopes */}
                {integration.supportedScopes && integration.supportedScopes.length > 0 && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-foreground">Data Scopes</label>
                    <div className="flex flex-wrap gap-2">
                      {integration.supportedScopes.map((scope) => (
                        <button
                          key={scope}
                          onClick={() => toggleScope(scope)}
                          disabled={isSubmitting}
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                            selectedScopes.includes(scope)
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:border-primary/50"
                          } disabled:opacity-50`}
                        >
                          {scope.replace(/_/g, " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sync Schedule */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Sync Schedule</label>
                  <select
                    value={syncSchedule}
                    onChange={(e) => setSyncSchedule(e.target.value)}
                    disabled={isSubmitting}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                  >
                    {SCHEDULE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Security Note */}
              <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                <Shield className="h-3.5 w-3.5 shrink-0" />
                Credentials are encrypted and stored in Google Cloud Secret Manager. They are never stored in the database.
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {phase === "saving" && (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Saving...
                    </>
                  )}
                  {phase === "testing" && (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Testing Connection...
                    </>
                  )}
                  {phase === "syncing" && (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Syncing Data...
                    </>
                  )}
                  {phase === "importing" && (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Importing Study Design...
                    </>
                  )}
                  {(phase === "form" || (phase === "error" && error)) && (isEditMode ? "Save & Test" : "Configure & Test")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
